import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NewsletterApi from "../../Api/NewsletterApi";
import CommonApi from "../../Api/CommonApi";
import FormApi from "../../Api/FormApi";
import EventApi from "../../Api/EventApi";
import { toast } from "react-toastify";
import Editor from "../Common/Editor";
import { NewsletterDto } from "../../Models/NewsletterDto";
import { SubscriptionWithMemberCount } from "../../Models/SubscriptionWithMemberCount";
import { FormDto } from "../../Models/FormDto";
import { Event } from "../../Models/Event";
import { newsletterHtmlToBr } from "../../Utils/newsletterHtml";

const NewsletterEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [newsletter, setNewsletter] = useState<NewsletterDto>({
    name: "",
    subject: "",
    html: "",
    dateScheduled: "",
    dateSent: null,
    subscriptionIds: [],
    sendToAll: false,
    formIds: [],
    eventIds: [],
  });

  const [editorHtml, setEditorHtml] = useState("");
  const [subscriptions, setSubscriptions] = useState<
    SubscriptionWithMemberCount[]
  >([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<number[]>(
    []
  );
  const [sendToAll, setSendToAll] = useState(false);
  const [forms, setForms] = useState<FormDto[]>([]);
  const [selectedForms, setSelectedForms] = useState<number[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);

  useEffect(() => {
    // Load subscriptions with member counts
    CommonApi.getSubscriptionsWithMemberCounts().then((res) => {
      // Filter out subscriptions without valid annual fee and with member count > 0
      const validSubscriptions = res.data.filter(
        (sub) => sub.annualFee && sub.annualFee > 0 && sub.memberCount > 0
      );
      setSubscriptions(validSubscriptions);
    });

    // Load forms
    FormApi.getAllForms().then((res) => {
      setForms(res.data);
    });

    // Load events
    EventApi.getEventList().then((res) => {
      setEvents(res.data);
    });

    if (id) {
      NewsletterApi.getById(Number(id)).then((res) => {
        const data = res.data;
        setNewsletter({
          id: data.id,
          name: data.name || "",
          subject: data.subject || "",
          html: data.html || "",
          dateScheduled: data.dateScheduled || "",
          dateSent: data.dateSent || null,
          subscriptionIds: data.subscriptionIds || [],
          sendToAll: data.sendToAll || false,
          formIds: data.formIds || [],
          eventIds: data.eventIds || [],
        });
        setEditorHtml(newsletterHtmlToBr(data.html || ""));
        setSelectedSubscriptions(data.subscriptionIds || []);
        setSendToAll(data.sendToAll || false);
        setSelectedForms(data.formIds || []);
        setSelectedEvents(data.eventIds || []);
      });
    }
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewsletter((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubscriptionChange = (
    subscriptionId: number,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedSubscriptions((prev) => [...prev, subscriptionId]);
    } else {
      setSelectedSubscriptions((prev) =>
        prev.filter((id) => id !== subscriptionId)
      );
    }
  };

  const handleSendToAllChange = (checked: boolean) => {
    setSendToAll(checked);
    if (checked) {
      setSelectedSubscriptions([]);
    }
  };

  const handleFormChange = (formId: number, checked: boolean) => {
    if (checked) {
      setSelectedForms((prev) => [...prev, formId]);
    } else {
      setSelectedForms((prev) => prev.filter((id) => id !== formId));
    }
  };

  const handleEventChange = (eventId: number, checked: boolean) => {
    if (checked) {
      setSelectedEvents((prev) => [...prev, eventId]);
    } else {
      setSelectedEvents((prev) => prev.filter((id) => id !== eventId));
    }
  };

  const buildPayload = (): NewsletterDto => {
    let scheduledDate = null;
    if (newsletter.dateScheduled) {
      const localDate = new Date(newsletter.dateScheduled);
      scheduledDate = localDate.toISOString();
    }

    return {
      ...newsletter,
      html: newsletterHtmlToBr(editorHtml),
      dateScheduled: scheduledDate,
      subscriptionIds: sendToAll ? [] : selectedSubscriptions,
      sendToAll: sendToAll,
      formIds: selectedForms,
      eventIds: selectedEvents,
    };
  };

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      await NewsletterApi.createOrUpdate(payload);

      // Keep editor in sync with normalized BR markup so reopen/save is stable
      setEditorHtml(payload.html || "");
      setNewsletter((prev) => ({ ...prev, html: payload.html || "" }));

      toast.success("Newsletter saved!");
      navigate("/admin/newsletters");
    } catch (err: any) {
      toast.error(
        "Error saving newsletter: " + (err.message || "Unknown error")
      );
    }
  };

  const formatDateTimeLocal = (isoDate?: string | null): string => {
    if (!isoDate) return "";
    if (
      isoDate.length === 16 &&
      isoDate.includes("T") &&
      !isoDate.includes("Z") &&
      !isoDate.includes("+")
    ) {
      return isoDate;
    }
    const date = new Date(isoDate);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSendTest = async () => {
    try {
      const payload = buildPayload();
      const response = await NewsletterApi.createOrUpdate(payload);

      const savedId =
        (id ? Number(id) : undefined) ||
        newsletter.id ||
        (response.data as NewsletterDto | undefined)?.id;

      if (!savedId) {
        toast.error("Failed to save newsletter before sending test");
        return;
      }

      const normalizedHtml = payload.html || "";
      setEditorHtml(normalizedHtml);
      setNewsletter((prev) => ({
        ...prev,
        id: savedId,
        html: normalizedHtml,
      }));

      await NewsletterApi.sendTestEmail(savedId);
      toast.success("Newsletter saved and test email sent!");

      if (!id) {
        navigate(`/newsletters/edit/${savedId}`);
      }
    } catch (err: any) {
      toast.error("Error sending test: " + (err.message || "Unknown error"));
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">
        {id ? "Edit Newsletter" : "Create Newsletter"}
      </h1>

      <div className="space-y-4">
        <div>
          <label className="block font-medium mb-1">Name</label>
          <input
            name="name"
            value={newsletter.name}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Subject</label>
          <input
            name="subject"
            value={newsletter.subject}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-medium mb-1">HTML Body</label>
          <Editor
            id="newsletterHtml"
            name="newsletterHtml"
            value={editorHtml}
            changedValue={setEditorHtml}
            className="w-full"
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Schedule Date</label>
          <input
            type="datetime-local"
            className="w-full p-2 border rounded"
            value={formatDateTimeLocal(newsletter.dateScheduled)}
            onChange={(e) =>
              setNewsletter({ ...newsletter, dateScheduled: e.target.value })
            }
          />
        </div>

        <div className="mt-2">
          <label className="block font-medium mb-1">Is Sent</label>
          <label className="inline-flex items-center">
            <input
              disabled
              type="checkbox"
              checked={!!newsletter.dateSent}
              onChange={(e) =>
                setNewsletter({
                  ...newsletter,
                  dateSent: e.target.checked ? new Date().toISOString() : null,
                })
              }
            />
            <span className="ml-2">Yes</span>
          </label>
        </div>

        <div className="mt-4">
          <label className="block font-medium mb-2">Subscription Levels</label>
          <p className="text-sm text-gray-600 mb-2">
            Used only when no form/event filter is selected. If you select forms
            or events below, those submitters/attendees are the audience
            (including non-members) — subscription checkboxes are ignored.
          </p>

          <div className="mb-3">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={sendToAll}
                disabled={selectedForms.length > 0 || selectedEvents.length > 0}
                onChange={(e) => handleSendToAllChange(e.target.checked)}
                className="mr-2"
              />
              <span className="font-medium">Send to All Members</span>
            </label>
            <p className="text-sm text-gray-600 ml-6">
              {selectedForms.length > 0 || selectedEvents.length > 0
                ? "Disabled while form/event filters are selected — audience is form/event participants."
                : "Send to all members regardless of subscription level"}
            </p>
          </div>

          {!sendToAll && selectedForms.length === 0 && selectedEvents.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-2">
                Select specific subscription levels to send to:
              </p>
              {subscriptions.map((subscription) => (
                <label
                  key={subscription.id}
                  className="flex items-center space-x-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedSubscriptions.includes(subscription.id)}
                    onChange={(e) =>
                      handleSubscriptionChange(
                        subscription.id,
                        e.target.checked
                      )
                    }
                    className="mr-2"
                  />
                  <span>{subscription.name}</span>
                  <span className="text-sm text-gray-500">
                    ({subscription.memberCount} member
                    {subscription.memberCount !== 1 ? "s" : ""})
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="block font-medium mb-2">Filter by Forms (Optional)</label>
          <p className="text-sm text-gray-600 mb-2">
            Target everyone who submitted the selected form(s), including guests /
            public submitters — not only members.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto border p-3 rounded">
            {forms.map((form) => (
              <label key={form.formId} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedForms.includes(form.formId!)}
                  onChange={(e) =>
                    handleFormChange(form.formId!, e.target.checked)
                  }
                  className="mr-2"
                />
                <span>{form.name}</span>
              </label>
            ))}
            {forms.length === 0 && (
              <p className="text-sm text-gray-500">No forms available</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block font-medium mb-2">Filter by Events (Optional)</label>
          <p className="text-sm text-gray-600 mb-2">
            Target newsletter to people attending specific events. When selected, only those registered for the event(s) will receive the email.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto border p-3 rounded">
            {events.map((event) => (
              <label key={event.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedEvents.includes(event.id)}
                  onChange={(e) =>
                    handleEventChange(event.id, e.target.checked)
                  }
                  className="mr-2"
                />
                <span>{event.name}</span>
                {event.eventDate && (
                  <span className="text-sm text-gray-500">
                    ({new Date(event.eventDate).toLocaleDateString()})
                  </span>
                )}
              </label>
            ))}
            {events.length === 0 && (
              <p className="text-sm text-gray-500">No events available</p>
            )}
          </div>
        </div>

        <button
          onClick={handleSendTest}
          className="px-4 py-2 bg-purple-600 text-white rounded ml-2"
        >
          Send Test Email
        </button>
        {newsletter.dateSent &&
          (selectedForms.length > 0 || selectedEvents.length > 0) && (
            <button
              onClick={async () => {
                if (!newsletter.id) return;
                if (
                  !window.confirm(
                    "Send to remaining form/event recipients who have not received this newsletter yet?"
                  )
                ) {
                  return;
                }
                try {
                  const res = await NewsletterApi.sendNow(newsletter.id, true);
                  const count = (res.data as { sentCount?: number })?.sentCount;
                  toast.success(`Sent to ${count ?? 0} remaining recipient(s).`);
                } catch (err: any) {
                  toast.error(
                    err?.response?.data ||
                      err.message ||
                      "Failed to send remaining recipients"
                  );
                }
              }}
              className="px-4 py-2 bg-green-700 text-white rounded ml-2"
            >
              Send remaining recipients
            </button>
          )}
        <br></br>
        <br></br>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Save Newsletter
        </button>
      </div>
    </div>
  );
};

export default NewsletterEditorPage;
